// ── My Cohorts ("/my-cohorts") — the coach/participant scoped list (Wave 3) ──
// ONE honest read (cohorts.list, server-scoped to the caller). The backend pins
// the scope (a coach sees assigned cohorts; a participant sees own cohorts only)
// — the frontend cannot widen it. This is a READ-ONLY surface: NO operational
// controls (no create/edit/complete), NO peer private detail. Each row links to
// the cohort workspace, where the participant-isolation + report-exclusion gates
// continue to apply server-side.
//
// ROLE-GATING: the route manifest's `my-cohorts` route is roles:["coach",
// "participant"] — every other role is route-denied (generic-not-found). Coach
// also has the operational /cohorts module; participant has ONLY this one.
//
// Routed through useHonestQuery -> <HonestState>: a gate / permission-denied /
// unavailable / empty read renders honestly, NEVER a fabricated empty list.

import { CollectionToolbar, PageFrame, ProTable } from "@vjal/ui";
import { useMemo } from "react";

import { HonestState, useHonestQuery } from "../../components/honest-state";
import { getCoachingServices } from "../../services/coaching-service-provider";
import { coachingQueryKeys } from "../../hooks/coaching-query-keys";
import type { CoachingCohortDto, ListResponse } from "../../services/coaching-dtos";
import type { ProductScreenContext } from "../product-route-contracts";
import { useMyCohortsCollection } from "./my-cohorts-collection";

const services = getCoachingServices();

export function MyCohortsPage({ role, routePath }: ProductScreenContext) {
  const query = useHonestQuery<ListResponse<CoachingCohortDto>>(
    coachingQueryKeys.list("cohorts.list", { scope: "mine" }),
    () => services.cohorts.list({ scope: "mine" })
  );
  const allRows = useMemo(() => query.data?.items ?? [], [query.data]);
  const isParticipant = role === "participant";
  const collection = useMyCohortsCollection(allRows, { linkToDetail: !isParticipant }, routePath);

  const emptyTitle = "No cohorts yet";
  const emptyMessage = isParticipant
    ? "You'll see your cohorts here once you're enrolled."
    : "You'll see your assigned cohorts here once they're created.";

  const posture =
    query.posture === null && collection.total === 0 && allRows.length > 0 ? "empty" : query.posture;
  const gated = posture !== null;
  const showToolbar = query.posture === null || posture === "empty";

  return (
    <PageFrame title="My cohorts" subtitle="Cohorts assigned to you — read-only operational scope." className="col-span-12">
      <section aria-label="My cohorts" className="flex flex-col gap-4" data-testid="my-cohorts-page">
        {showToolbar && gated ? (
          <CollectionToolbar
            collection={collection}
            searchPlaceholder="Search by name, company, program, or coach…"
            className="rounded-xl border border-line"
            stableChipNames
          />
        ) : null}

        <div className="rounded-xl border border-line bg-white">
          {gated ? (
            <HonestState
              posture={posture ?? "loading"}
              gate={query.gate}
              reference={query.reference}
              onRetry={query.retry}
              title={posture === "empty" ? emptyTitle : undefined}
              message={posture === "empty" ? emptyMessage : undefined}
            />
          ) : (
            /* F3: real @vjal/ui ProTable-1 recipe export under Page-1 shell. */
            <ProTable
              collection={collection}
              className="p-0"
              tableProps={{
                caption: "Your cohorts",
                emptyTitle,
                emptyMessage,
              }}
              stableChipNames
            />
          )}
        </div>
      </section>
    </PageFrame>
  );
}